import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Box,
  Chip,
  Container,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  Typography,
} from "@mui/material";
import {
  ArrowDownUp,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";
import EmptyTable from "../components/EmptyTable";
import SearchTextInput from "../components/inputs/SearchTextInput";
import ToggleInput from "../components/inputs/ToggleInput";
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
} from "../components/Tables";
import { useApi } from "../contexts/ApiContext";
import { useApp } from "../contexts/AppContext";
import { colors, fontSizes } from "../theme";
import { SortType } from "./types";
import { isRtlLanguage } from "../utils";

interface Column<T> {
  id: keyof T;
  label: string;
  sortable?: boolean;
  width?: string | number;
}

interface HistoryProps<T> {
  title: string;
  columns: Column<T>[];
  data: T[] | null;
  onItemClick: (row: T) => void;
  disabledId?: string | null;
  pagintation?: {
    page: number;
    total: number;
    perPage: number;
  };
  onSearch?: (
    sort: SortType<T> | null,
    patient: string,
    review: boolean,
  ) => void;
  isLoadingData?: boolean;
}

const History = <T extends { actualOutcome: string }>({
  title,
  columns,
  data,
  onItemClick,
  onSearch,
  disabledId,
  isLoadingData = false,
}: HistoryProps<T>) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sort, setSort] = useState<SortType<T> | null>(null);
  const [showOnlyRequiredReview, setShowOnlyRequiredReview] =
    useState<boolean>(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const { translate, currentLanguage } = useApp();
  const generalTranslations = translate("general");

  const resourceName = window.location.pathname.split("/")[1];

  const { isLoading } = useApi();

  const handleSortToggle = (column: keyof T) => {
    if (sort?.column === column) {
      if (sort.direction === "asc") {
        setSort({ column, direction: "desc" });
      } else {
        setSort(null);
      }
      setSort({ column, direction: "asc" });
      return;
    }

    setSort({ column, direction: "asc" });
  };

  useEffect(() => {
    console.log(
      "abs - Fetching data",
      sort,
      searchTerm,
      showOnlyRequiredReview
    );
    onSearch?.(sort, searchTerm, showOnlyRequiredReview);
  }, [sort, searchTerm, showOnlyRequiredReview]);

  console.info("abs data", data);

  return (
    <Container
      sx={{
        minHeight: "100vh",
        maxWidth: "1800px!important",
        paddingLeft: "0!important",
        paddingRight: "0!important",
      }}
    >
      <Box pb={theme.spacing(2)}>
        <Typography
          color={colors.stone[600]}
          fontSize={fontSizes["2xl"]}
          fontWeight={"medium"}
        >
          {title}
        </Typography>
      </Box>

      <>
        {(data ?? []).length === 0 &&
        !isLoadingData &&
        !searchTerm.length &&
        !showOnlyRequiredReview ? (
          <EmptyTable onClick={() => navigate(`/${resourceName}/create`)} />
        ) : (
          <React.Fragment>
            <Stack width={700} marginBottom={4} direction={"row"} gap={2}>
              <SearchTextInput
                label=""
                placeholder={generalTranslations.history.search.patient}
                value={searchTerm}
                onChange={setSearchTerm}
              />
              <Box width={"100%"}>
                <ToggleInput
                  label={generalTranslations.history.search.requiredReview}
                  value={showOnlyRequiredReview}
                  onChange={(value) => setShowOnlyRequiredReview(value)}
                />
              </Box>
            </Stack>
            <StyledTableContainer>
              <Table className={isLoadingData ? "loading" : ""}>
                <StyledTableHead>
                  <StyledTableRow>
                    {columns.map((column) => (
                      <TableCell key={String(column.id)} width={column.width}>
                        <Stack
                          direction={"row"}
                          spacing={1}
                          alignItems={"center"}
                        >
                          <Typography
                            fontWeight={"medium"}
                            color={colors.stone[500]}
                            fontSize={fontSizes.sm}
                          >
                            {column.label}
                          </Typography>
                          {sort?.column === column.id ? (
                            sort.direction === "asc" ? (
                              <ArrowDownWideNarrow
                                color={colors.stone[500]}
                                width={20}
                              />
                            ) : (
                              <ArrowUpWideNarrow
                                color={colors.stone[500]}
                                width={20}
                              />
                            )
                          ) : (
                            <>
                              {column.sortable && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleSortToggle(column.id)}
                                >
                                  <ArrowDownUp
                                    color={colors.stone[500]}
                                    width={20}
                                  />
                                </IconButton>
                              )}
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    ))}
                    <TableCell>
                      {generalTranslations.history.columns.actualOutcome}
                    </TableCell>
                    <TableCell></TableCell>
                  </StyledTableRow>
                </StyledTableHead>
                <TableBody>
                  {(data ?? []).map((row, index) => (
                    <StyledTableRow
                      onClick={() => {
                        if (disabledId === (row as any).id) {
                          return;
                        }
                        onItemClick(row);
                      }}
                      key={index}
                    >
                      {columns.map((column) => (
                        <TableCell key={String(column.id)}>
                          <Typography
                            fontSize={fontSizes.sm}
                            color={colors.stone[900]}
                          >
                            {(row as any)[column.id]}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell>
                        {row.actualOutcome === "Review required" ? (
                          <Chip
                            sx={{
                              backgroundColor: colors.violet[100],
                              fontWeight: "medium",
                              fontSize: fontSizes.sm,
                              color: colors.stone[900],
                            }}
                            label={row.actualOutcome}
                          />
                        ) : (
                          <Typography
                            color={colors.stone[900]}
                            fontSize={fontSizes.sm}
                          >
                            {row.actualOutcome}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => {}}>
                          <ArrowForwardIosIcon sx={{ fontSize: 18, transform: isRtlLanguage(currentLanguage) ? "rotate(180deg)" : "" }} />
                        </IconButton>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </React.Fragment>
        )}
      </>
    </Container>
  );
};

export default History;
